'use client';
import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { ADMIN_ROLE, HR_ROLE } from '@/config/app.config';
import {
  adminNavbar,
  nonUserNavbar,
  userNavbar,
} from '@/lib/constant/app.constant';
import icons from '@/lib/icons';
import { cn, getNameInitials } from '@/lib/utils';
import { Menu, X, Briefcase, Bell, Moon, Sun } from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import { useTheme } from 'next-themes';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

export const CompanyLogo = () => {
  return (
    <div className="flex items-center gap-2">
      <Briefcase className="h-6 w-6 text-primary" />
      <span className="font-display text-xl font-semibold">JobAssure</span>
    </div>
  );
};

const Header = () => {
  const session = useSession();
  const { theme, setTheme } = useTheme();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    setMounted(true);

    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full',
          isScrolled
            ? 'bg-white/80 dark:bg-background/80 backdrop-blur-md shadow-sm py-3'
            : 'bg-white/80 dark:bg-background/80 py-5'
        )}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link href="/" className="flex items-center gap-2">
                <CompanyLogo />
              </Link>

              <nav className="hidden md:flex ml-10 space-x-8">
                {session.status === 'loading'
                  ? nonUserNavbar.map((_, index) => (
                    <Skeleton className="h-4 w-[60px]" key={index} />
                  ))
                  : session.data?.user
                    ? session.data?.user.role === ADMIN_ROLE ||
                      session.data?.user.role === HR_ROLE
                      ? adminNavbar.map((item) => (
                        <Link
                          href={item.path}
                          key={item.id}
                          className="text-foreground/80 hover:text-primary dark:hover:text-blue-400 transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))
                      : userNavbar.map((item) => (
                        <Link
                          href={item.path}
                          key={item.id}
                          className="text-foreground/80 hover:text-primary dark:hover:text-blue-400 transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))
                    : nonUserNavbar.map((item) => (
                      <Link
                        href={item.path}
                        key={item.id}
                        className="text-foreground/80 hover:text-primary dark:hover:text-blue-400 transition-colors"
                      >
                        {item.label}
                      </Link>
                    ))}
              </nav>
            </div>

            <div className="hidden md:flex items-center space-x-4">


              {session.status === 'loading' ? (
                <Skeleton className="h-8 w-8 rounded-full" />
              ) : session.status === 'authenticated' ? (
                <>
                  <Link
                    href="/notifications"
                    aria-label="Notifications"
                    className="text-foreground/70 hover:text-primary dark:hover:text-blue-400 transition-colors"
                  >
                    <Bell className="h-5 w-5" />
                  </Link>
                  <DropdownMenu open={open} onOpenChange={setOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="relative h-8 w-8 rounded-full"
                        aria-label="avatar"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage
                            src={
                              session.data.user.image
                                ? session.data.user.image
                                : 'hello'
                            }
                          />
                          <AvatarFallback>
                            {getNameInitials(session.data.user.name)}
                          </AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-56"
                      align="end"
                      forceMount
                    >
                      <DropdownMenuItem>
                        <icons.profile className="mr-2 h-4 w-4" />
                        <Link
                          className="w-full"
                          href={
                            session.data?.user?.id
                              ? `/profile/${session.data.user.id}`
                              : '/signin'
                          }
                        >
                          Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          signOut();
                        }}
                      >
                        <icons.logout className="mr-2 h-4 w-4" />
                        <span>Log out</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button
                    className="text-sm"
                    onClick={() => router.push('/signin')}
                  >
                    Sign In
                  </Button>
                </div>
              )}
              {mounted && (
                <button
                  className="text-foreground/70 hover:text-primary dark:hover:text-blue-400 transition-colors"
                  onClick={toggleTheme}
                  aria-label="theme"
                >
                  {theme === 'dark' ? (
                    <Moon className="h-5 w-5" />
                  ) : (
                    <Sun className="h-5 w-5" />
                  )}
                </button>
              )}
            </div>

            <button
              className="md:hidden text-foreground/70"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 top-16 bg-background z-40 md:hidden animate-in">
            <nav className="flex flex-col p-6 space-y-6">
              <Link href="#"
                className="text-lg font-medium flex items-center gap-2"
                onClick={toggleTheme}
              >
                {theme === 'dark' ? (
                  <>
                    <span>Light</span>
                    <Moon className="h-5 w-5" />

                  </>
                ) : (
                  <>
                    <span>Dark</span>

                    <Sun className="h-5 w-5" />
                  </>
                )}
              </Link>
              {session.status === 'loading'
                ? nonUserNavbar.map((_, index) => (
                  <Skeleton className="h-4 w-[60px]" key={index} />
                ))
                : session.data?.user
                  ? session.data?.user.role === ADMIN_ROLE ||
                    session.data?.user.role === HR_ROLE
                    ? adminNavbar.map((item) => (
                      <Link
                        href={item.path}
                        key={item.id}
                        className="text-lg font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))
                    : userNavbar.map((item) => (
                      <Link
                        href={item.path}
                        key={item.id}
                        className="text-lg font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))
                  : nonUserNavbar.map((item) => (
                    <Link
                      href={item.path}
                      key={item.id}
                      className="text-lg font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
              <hr className="border-border" />
              {session.status === 'authenticated' ? (
                <>
                  <Link
                    href={
                      session.data?.user?.id
                        ? `/profile/${session.data.user.id}`
                        : '/signin'
                    }
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg font-medium"
                  >
                    Profile
                  </Link>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      signOut();
                      setMobileMenuOpen(false);
                    }}
                  >
                    Log out
                  </Button>
                </>
              ) : (
                <>
                  <Link href="/signin" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full">
                      Sign In
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </header>
      <div className="h-[42px] print:hidden"></div>
    </>
  );
};

export default Header;
